import type { GetRecipeByIdQuery } from '@schema/cart';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import Lightbox from '@components/Lightbox';
import icons from '@icons';
import { Picture } from '@ui';
import { twcls } from '@utils/twcls';
import UIicons from '@ui-kit';
import { createImageErrorHandler } from '@utils/onImageError';
import { observer } from 'mobx-react';
import { useStores } from '@root/src/stores';

const { productCardErrorImg } = UIicons;

const {
  bag: { white: bagWhite3d }
} = icons.threeD;
const {
  maximize: { white: maxWhiteOutlined }
} = icons.Outline;

const onImageErrorHandler = createImageErrorHandler(productCardErrorImg.x3);

interface RecipeStepsProps {
  isMobile?: boolean;
  video_code: string | null | undefined;
  isWithoutPhotos?: boolean;
  steps: NonNullable<GetRecipeByIdQuery['getRecipeById']>['recipeInstructions'];
}
const RecipeSteps: FC<RecipeStepsProps> = observer(
  ({ steps, video_code, isWithoutPhotos, isMobile }) => {
    const { modalsStore } = useStores();
    const [stepsWithImageError, setStepsWithImageError] = useState<number[]>(
      []
    );
    const [toggler, setToggler] = useState(false);
    const [activeSlideNumber, setActiveSlideNumber] = useState(0);
    const onImgClick = useCallback((index: number) => {
      setToggler(prev => !prev);
      setActiveSlideNumber(index + 1);
    }, []);

    return (
      <>
        <Lightbox
          toggler={toggler}
          slide={activeSlideNumber}
          sources={steps?.map((item, index) => {
            const src = stepsWithImageError.includes(index)
              ? productCardErrorImg.x3
              : item.mainImage || item.thumbnail || '';
            return <img key={item.id} src={src} alt={item?.title ?? ''} />;
          })}
          type='image'
        />
        <div
          className={twcls(
            'flex flex-col gap-y-6',
            isWithoutPhotos &&
              'bg-[var(--light-grey)] p-4 rounded-xl sm:flex-col-reverse allMd:p-4 allMd:pb-5'
          )}
        >
          <div className='flex items-center justify-between'>
            <div className='text-xl font-medium allMd:hidden'>
              Приготовление
            </div>
            {video_code && (
              <button
                onClick={() => {
                  modalsStore.openVideoModal({
                    url: video_code,
                    isDesktop: !isMobile
                  });
                }}
                className={twcls(`text-sm
                allMd:w-full
                h-10
                cursor-pointer
                px-7
                hover:border-[var(--dark-red)]
                border
                border-solid
                border-[var(--main-font-color)]
                rounded-lg
                hover:bg-[var(--dark-red)]
                bg-[var(--light-grey)]
                transition-colors
                hover:text-white`)}
              >
                Смотреть видео
              </button>
            )}
          </div>
          <ul className='flex flex-col gap-y-6'>
            {!!steps?.length &&
              steps.map((step, index) => (
                <li
                  id={step?.id}
                  key={step.id}
                  data-testid='recipe-step'
                  className={twcls(
                    'grid grid-cols-2 gap-x-5 sm:block',
                    isWithoutPhotos && 'flex sm:flex'
                  )}
                >
                  {!isWithoutPhotos && (
                    <div
                      onClick={() => {
                        if (step?.thumbnail) {
                          onImgClick(index);
                        }
                      }}
                      className={twcls(
                        `sm:mb-3 relative w-full h-[212px] group flex items-center justify-center rounded-2xl overflow-hidden cursor-default`,
                        step?.thumbnail && 'cursor-pointer'
                      )}
                    >
                      {step?.thumbnail && (
                        <div className='absolute flex items-center justify-center w-10 h-10 transition-opacity rounded-full opacity-0 bg-ultra-dark-grey/30 group-hover:opacity-100'>
                          <Picture
                            icon={maxWhiteOutlined}
                            width='24'
                            height='24'
                          />
                        </div>
                      )}
                      <div className='absolute flex items-center justify-center w-8 h-8 text-white rounded-full bottom-4 right-4 bg-ultra-dark-grey/30'>
                        {index + 1}
                      </div>
                      {step?.thumbnail ? (
                        <div className='w-full h-full'>
                          <img
                            onError={e => {
                              onImageErrorHandler(e);
                              setStepsWithImageError(prev => [...prev, index]);
                            }}
                            className='object-cover w-full h-full rounded-2xl'
                            src={step?.thumbnail}
                            alt={step?.title ?? ''}
                          />
                        </div>
                      ) : (
                        <div className='flex w-full h-full items-center justify-center rounded-2xl bg-[var(--ultra-light-grey)]'>
                          <Picture icon={bagWhite3d} width='140' height='140' />
                        </div>
                      )}
                    </div>
                  )}
                  {isWithoutPhotos && (
                    <div className='flex-shrink-0 w-8 h-8 flex items-center text-xl justify-center bg-transparent border border-solid border-[var(--dark-grey)] text-[var(--main-font-color)] font-medium leading-none rounded-lg'>
                      {index + 1}
                    </div>
                  )}
                  <div className='text-sm'>{step?.title}</div>
                </li>
              ))}
          </ul>
        </div>
      </>
    );
  }
);

export default RecipeSteps;
